import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Placeholder from '@tiptap/extension-placeholder';
import 'katex/dist/katex.min.css';

import { FontSize } from './richtext/FontSize';
import { MathInline } from './richtext/MathInline';
import { ResizableImage } from './richtext/ResizableImage';
import MathDialog from './richtext/MathDialog';
import { api } from '../api/client';
import './richtext/richtext.css';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

// Common web-safe fonts shown by default. On Chromium browsers the dropdown is
// enriched with every locally-installed font via the Local Font Access API.
const DEFAULT_FONTS: Array<[string, string]> = [
  ['', 'Default font'],
  ['Arial, sans-serif', 'Arial'],
  ['"Helvetica Neue", Helvetica, sans-serif', 'Helvetica'],
  ['Verdana, sans-serif', 'Verdana'],
  ['Tahoma, sans-serif', 'Tahoma'],
  ['"Trebuchet MS", sans-serif', 'Trebuchet MS'],
  ['Georgia, serif', 'Georgia'],
  ['"Times New Roman", serif', 'Times New Roman'],
  ['Garamond, serif', 'Garamond'],
  ['"Courier New", monospace', 'Courier New'],
  ['"Comic Sans MS", cursive', 'Comic Sans'],
  ['Calibri, sans-serif', 'Calibri'],
  ['Cambria, serif', 'Cambria'],
];
const SIZES = ['', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '40px', '48px'];

/** Wrap a family name in quotes when it contains spaces, for use in a font-family value. */
function fontValue(family: string): string {
  return /\s/.test(family) ? `"${family}"` : family;
}

function Btn({
  editor, onClick, active, title, children, disabled,
}: {
  editor: Editor;
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  // editor passed only to force re-render via key in parent; click handler does the work.
  void editor;
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      className={active ? 'is-active' : ''}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fonts, setFonts] = useState<Array<[string, string]>>(DEFAULT_FONTS);
  const fontsLoaded = useRef(false);
  // Math dialog state. `apply` receives the confirmed LaTeX (insert new, or update existing).
  const [mathDialog, setMathDialog] = useState<
    { open: boolean; latex: string; chemistry: boolean; apply: (l: string) => void }
  >({ open: false, latex: '', chemistry: false, apply: () => {} });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      ResizableImage.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      MathInline.configure({
        onEdit: (latex, apply) => setMathDialog({ open: true, latex, chemistry: /^\\ce\{/.test(latex), apply }),
      }),
      Placeholder.configure({ placeholder: placeholder || 'Write the question…' }),
    ],
    content: value || '',
    editorProps: { attributes: { class: 'rte-doc' } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Pull every installed font (Chromium only; requires a user gesture + permission).
  const loadSystemFonts = useCallback(async () => {
    if (fontsLoaded.current) return;
    fontsLoaded.current = true;
    const q = (window as unknown as { queryLocalFonts?: () => Promise<Array<{ family: string }>> }).queryLocalFonts;
    if (!q) return;
    try {
      const available = await q();
      const families = Array.from(new Set(available.map((f) => f.family))).sort();
      if (families.length) {
        setFonts([['', 'Default font'], ...families.map((f) => [fontValue(f), f] as [string, string])]);
      }
    } catch {
      /* permission denied — keep the curated list */
    }
  }, []);

  // Keep the editor in sync when the parent swaps in a different question's prompt.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const onPickImage = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !editor) return;
      try {
        const { url } = await api.uploadPromptImage(file, file.type);
        editor.chain().focus().setImage({ src: url }).run();
      } catch (err) {
        alert('Image upload failed: ' + (err instanceof Error ? err.message : 'unknown error'));
      }
    },
    [editor],
  );

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL (leave blank to remove):', prev || '');
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const openMathDialog = (chem: boolean) => {
    setMathDialog({
      open: true,
      latex: '',
      chemistry: chem,
      apply: (latex) => editor.chain().focus().insertMath(latex, false).run(),
    });
  };

  const blockTag = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
    ? 'h2'
    : editor.isActive('heading', { level: 3 })
    ? 'h3'
    : 'p';

  return (
    <div className="rte">
      <div className="rte-toolbar">
        <select
          value={blockTag}
          title="Paragraph style"
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run();
          }}
        >
          <option value="p">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>

        <select
          title="Font"
          value={(editor.getAttributes('textStyle').fontFamily as string) || ''}
          onMouseDown={loadSystemFonts}
          onFocus={loadSystemFonts}
          onChange={(e) => {
            const f = e.target.value;
            if (f) editor.chain().focus().setFontFamily(f).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
        >
          {fonts.map(([v, label]) => (
            <option key={label} value={v} style={{ fontFamily: v || undefined }}>{label}</option>
          ))}
        </select>

        <select
          title="Font size"
          value={(editor.getAttributes('textStyle').fontSize as string) || ''}
          onChange={(e) => {
            const s = e.target.value;
            if (s) editor.chain().focus().setFontSize(s).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
        >
          {SIZES.map((s) => (
            <option key={s || 'default'} value={s}>{s || 'Size'}</option>
          ))}
        </select>

        <span className="rte-sep" />

        <Btn editor={editor} title="Bold (Ctrl+B)" active={editor.isActive('bold')}
             onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></Btn>
        <Btn editor={editor} title="Italic (Ctrl+I)" active={editor.isActive('italic')}
             onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></Btn>
        <Btn editor={editor} title="Underline (Ctrl+U)" active={editor.isActive('underline')}
             onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></Btn>
        <Btn editor={editor} title="Strikethrough" active={editor.isActive('strike')}
             onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></Btn>

        <input
          type="color"
          title="Text color"
          value={(editor.getAttributes('textStyle').color as string) || '#000000'}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />

        <span className="rte-sep" />

        <Btn editor={editor} title="Bullet list" active={editor.isActive('bulletList')}
             onClick={() => editor.chain().focus().toggleBulletList().run()}>• ⃪</Btn>
        <Btn editor={editor} title="Numbered list" active={editor.isActive('orderedList')}
             onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</Btn>

        <Btn editor={editor} title="Align left" active={editor.isActive({ textAlign: 'left' })}
             onClick={() => editor.chain().focus().setTextAlign('left').run()}>⯇</Btn>
        <Btn editor={editor} title="Align center" active={editor.isActive({ textAlign: 'center' })}
             onClick={() => editor.chain().focus().setTextAlign('center').run()}>≡</Btn>
        <Btn editor={editor} title="Align right" active={editor.isActive({ textAlign: 'right' })}
             onClick={() => editor.chain().focus().setTextAlign('right').run()}>⯈</Btn>

        <span className="rte-sep" />

        <Btn editor={editor} title="Link" active={editor.isActive('link')} onClick={setLink}>🔗</Btn>
        <Btn editor={editor} title="Insert image" onClick={() => fileRef.current?.click()}>🖼</Btn>
        <Btn editor={editor} title="Insert table"
             onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>▦</Btn>
        <Btn editor={editor} title="Add column" disabled={!editor.can().addColumnAfter()}
             onClick={() => editor.chain().focus().addColumnAfter().run()}>+col</Btn>
        <Btn editor={editor} title="Delete column" disabled={!editor.can().deleteColumn()}
             onClick={() => editor.chain().focus().deleteColumn().run()}>−col</Btn>
        <Btn editor={editor} title="Add row" disabled={!editor.can().addRowAfter()}
             onClick={() => editor.chain().focus().addRowAfter().run()}>+row</Btn>
        <Btn editor={editor} title="Delete row" disabled={!editor.can().deleteRow()}
             onClick={() => editor.chain().focus().deleteRow().run()}>−row</Btn>
        <Btn editor={editor} title="Delete table" disabled={!editor.can().deleteTable()}
             onClick={() => editor.chain().focus().deleteTable().run()}>⌦▦</Btn>

        <span className="rte-sep" />

        <Btn editor={editor} title="Insert formula (LaTeX)" onClick={() => openMathDialog(false)}>∑</Btn>
        <Btn editor={editor} title="Insert chemistry (\ce{})" onClick={() => openMathDialog(true)}>🧪</Btn>

        <span className="rte-sep" />

        <Btn editor={editor} title="Undo" disabled={!editor.can().undo()}
             onClick={() => editor.chain().focus().undo().run()}>↶</Btn>
        <Btn editor={editor} title="Redo" disabled={!editor.can().redo()}
             onClick={() => editor.chain().focus().redo().run()}>↷</Btn>
      </div>

      <EditorContent className="rte-content" editor={editor} />
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />

      <MathDialog
        open={mathDialog.open}
        initial={mathDialog.latex}
        chemistry={mathDialog.chemistry}
        title={mathDialog.chemistry ? 'Insert chemistry formula' : 'Insert formula'}
        onCancel={() => setMathDialog((d) => ({ ...d, open: false }))}
        onConfirm={(latex) => {
          mathDialog.apply(latex);
          setMathDialog((d) => ({ ...d, open: false }));
        }}
      />
    </div>
  );
}
